import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";

import { useToast } from "@chakra-ui/react";
import { LicenseManager } from "ag-grid-enterprise";

import Loader from "./components/Loader";
import AuthorizedRoutes from "./routers/AuthorizedRoutes";
import UnauthorizedRoutes from "./routers/UnAuthorizedRoutes";
import LoginService from "./services/loginService";
import { fetchSubOrganizations, getUser, verifyToken } from "./store/slices/loginSlices";
import { getPayoutOrderData } from "./store/slices/transactionSlices";
import { getItem } from "./utils/local_storage";
import { initSentry } from "./utils/sentry";

function AppContent() {
  const dispatch = useDispatch();
  const [authState, setAuthState] = useState("loading");
  const loginDetails = useSelector((state) => state.login?.loginDetails);
  const isLoading = useSelector((state) => state.login?.userDetails?.loading);
  const orgType = useSelector((state) => state?.login?.orgDetails?.orgType || "default");
  const orgId = useSelector((state) => state?.login?.orgDetails?.orgId);
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);

  const handleAutoLogin = useCallback(
    async (token) => {
      try {
        setIsVerifyingToken(true);
        console.log("Attempting auto-login with token:", token.substring(0, 10));

        const result = await dispatch(verifyToken({ token }));
        const data = result?.payload?.data || {};

        if (data?.status === "verified") {
          setAuthState("signedIn");

          toast({
            title: "Login successful",
            description: "Welcome back! You have been automatically logged in.",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
          navigate(location.pathname, { replace: true });
        } else {
          setAuthState("signIn");
          toast({
            title: "Auto-login failed",
            description: "Invalid or expired token. Please log in manually.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Auto-login failed:", error);
        setAuthState("signIn");
      } finally {
        setIsVerifyingToken(false);
      }
    },
    [dispatch, toast, navigate, location.pathname],
  );

  // Check for token in query parameters and handle existing tokens

  LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_KEY);
  const fetchUser = useCallback(() => {
    dispatch(getUser());
  }, [dispatch, orgId]);

  const fetchApprovals = useCallback(() => {
    dispatch(getPayoutOrderData({ filters: { status: "approval_pending" } }));
  }, [dispatch, orgId]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token");
    const existingToken = getItem("access-token");
    const authStatus = getItem("loggedIn");

    if (token) {
      if (existingToken) {
        localStorage.clear();
      }
      handleAutoLogin(token);
    } else {
      setAuthState(authStatus === "true" || loginDetails?.loggedIn ? "signedIn" : "signIn");
    }
  }, [location.search, handleAutoLogin]);

  useEffect(() => {
    const authStatus = getItem("loggedIn");
    if (loginDetails?.loggedIn || authStatus) {
      setAuthState("signedIn");
      dispatch(fetchSubOrganizations());
      if (orgType === "payout") {
        fetchApprovals();
      }
    } else if (!isVerifyingToken) {
      setAuthState("signIn");
    }
  }, [loginDetails, isVerifyingToken]);

  useEffect(() => {
    if (authState === "signedIn") {
      fetchUser();
      setInterval(async () => {
        await LoginService.heartBeat();
      }, 300000);
    }
  }, [authState]);

  return (
    <>
      {(isLoading || authState === "loading" || isVerifyingToken) && <Loader />}
      {authState === "signIn" && !isLoading && <UnauthorizedRoutes />}
      {authState === "signedIn" && !isLoading && <AuthorizedRoutes />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
